import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Store, Save, Check, ArrowRight, PlusCircle, CheckCircle2, Info, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  CATEGORIAS_DIGITALES,
  PAISES_MKT,
  MKT_GOLD,
  MKT_TINT,
} from "../../lib/marketplace";
import { apiGetTienda, apiCrearTienda, apiEditarTienda } from "../../lib/marketplaceApi";
import { humanizeAxiosError } from "../../lib/api";
import { Field, TextInput, TextArea, SelectInput, ImageUpload } from "./mktui";

export default function CrearTienda() {
  const navigate = useNavigate();
  const [editando, setEditando] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const [form, setForm] = useState({
    nombre: "", vendedor: "", descripcion: "", logo: "", portada: "",
    pais: "", ciudad: "", telefono: "", correo: "", web: "",
    categorias: [], terminos: false, estado: "borrador",
  });
  const [exito, setExito] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const t = await apiGetTienda();
        if (t) {
          setForm({ ...t, terminos: true });
          setEditando(true);
        }
      } catch (e) { /* sin tienda */ }
      finally { setCargando(false); }
    })();
  }, []);

  const toggleCat = (id) =>
    set("categorias", form.categorias.includes(id) ? form.categorias.filter((x) => x !== id) : [...form.categorias, id]);

  const validar = () => {
    if (!form.nombre.trim()) return "Ingresa el nombre de la tienda.";
    if (!form.vendedor.trim()) return "Ingresa el nombre público del vendedor.";
    if (!form.pais) return "Selecciona un país.";
    if (form.categorias.length === 0) return "Selecciona al menos una categoría.";
    if (!form.terminos) return "Debes aceptar los términos para vendedores.";
    return null;
  };

  const crear = async () => {
    const err = validar();
    if (err) {
      toast.error(err);
      return;
    }
    setGuardando(true);
    try {
      if (editando) {
        await apiEditarTienda(form);
        toast.success("Cambios guardados.");
        navigate("/marketplace");
      } else {
        await apiCrearTienda(form, "en_revision");
        setExito(true);
      }
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    } finally {
      setGuardando(false);
    }
  };

  const borrador = async () => {
    if (!form.nombre.trim()) return toast.error("Ingresa al menos el nombre de la tienda.");
    setGuardando(true);
    try {
      if (editando) await apiEditarTienda(form);
      else await apiCrearTienda(form, "borrador");
      toast.success("Tienda guardada como borrador.");
      navigate("/marketplace");
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return <div className="min-h-[calc(100vh-4rem)] bg-slate-50 flex items-center justify-center text-slate-500"><Loader2 className="animate-spin mr-2" /> Cargando…</div>;
  }

  if (exito) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-200 shadow-soft p-8 text-center" data-testid="tienda-exito">
          <span className="w-16 h-16 rounded-full mx-auto flex items-center justify-center" style={{ backgroundColor: "#DCFCE7", color: "#15803D" }}>
            <CheckCircle2 size={34} />
          </span>
          <h1 className="mt-5 text-2xl font-extrabold text-slate-900">¡Tu tienda ha sido creada correctamente!</h1>
          <p className="mt-2 text-sm text-slate-500">
            Tu tienda quedó <b>en revisión</b>. Ya puedes empezar a preparar tus productos digitales.
          </p>
          <div className="mt-7 flex flex-col gap-3">
            <button
              onClick={() => navigate("/marketplace")}
              data-testid="exito-ir-tienda"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-bold shadow-sm hover:brightness-110 transition-all"
              style={{ backgroundColor: MKT_GOLD }}
            >
              <Store size={18} /> Ir a Mi Tienda
            </button>
            <button
              onClick={() => navigate("/marketplace/productos/crear")}
              data-testid="exito-crear-producto"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-slate-300 text-slate-700 font-bold hover:bg-slate-100 transition-colors"
            >
              <PlusCircle size={18} /> Crear mi primer producto
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="crear-tienda-page">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800" data-testid="ct-volver-top">
          <ChevronLeft size={16} /> Volver
        </button>

        <div className="mt-5 flex items-center gap-4">
          <span className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm" style={{ backgroundColor: MKT_TINT, color: MKT_GOLD }}>
            <Store size={26} />
          </span>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: MKT_GOLD }}>ConstruLink Marketplace</p>
            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
              {editando ? "Editar tienda" : "Crea tu tienda en ConstruLink Marketplace"}
            </h1>
          </div>
        </div>
        <p className="mt-2 text-slate-600 max-w-2xl">
          Completa la información de tu negocio digital para comenzar a publicar productos.
        </p>

        {/* Imágenes */}
        <section className="mt-8 bg-white rounded-2xl border border-slate-200 shadow-soft p-6 space-y-5">
          <h2 className="text-sm font-bold uppercase tracking-widest text-slate-500">Identidad de la tienda</h2>
          <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-5">
            <Field label="Foto o logo de tienda" testid="ct-logo-field">
              <ImageUpload value={form.logo} onChange={(v) => set("logo", v)} scope="logo" aspect="aspect-square" label="Subir logo" testid="ct-logo" />
            </Field>
            <Field label="Imagen de portada" testid="ct-portada-field">
              <ImageUpload value={form.portada} onChange={(v) => set("portada", v)} scope="portada" aspect="aspect-[3/1]" label="Subir portada" testid="ct-portada" />
            </Field>
          </div>
        </section>

        {/* Datos básicos */}
        <section className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-soft p-6 space-y-5">
          <h2 className="text-sm font-bold uppercase tracking-widest text-slate-500">Datos del negocio</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Nombre de la tienda" required testid="ct-nombre-field">
              <TextInput value={form.nombre} onChange={(v) => set("nombre", v)} placeholder="Ej. Estudio Digital Constructor" testid="ct-nombre" />
            </Field>
            <Field label="Nombre público del vendedor" required testid="ct-vendedor-field">
              <TextInput value={form.vendedor} onChange={(v) => set("vendedor", v)} placeholder="Ej. Ing. Ana Torres" testid="ct-vendedor" />
            </Field>
          </div>
          <Field label="Descripción de la tienda" testid="ct-desc-field">
            <TextArea value={form.descripcion} onChange={(v) => set("descripcion", v)} rows={3} placeholder="Cuenta de qué trata tu negocio digital…" testid="ct-desc" />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="País" required testid="ct-pais-field">
              <SelectInput value={form.pais} onChange={(v) => set("pais", v)} options={PAISES_MKT} placeholder="Selecciona tu país" testid="ct-pais" />
            </Field>
            <Field label="Ciudad" testid="ct-ciudad-field">
              <TextInput value={form.ciudad} onChange={(v) => set("ciudad", v)} placeholder="Ej. Lima" testid="ct-ciudad" />
            </Field>
            <Field label="Teléfono de contacto" testid="ct-tel-field">
              <TextInput value={form.telefono} onChange={(v) => set("telefono", v)} placeholder="Ej. +51 999 999 999" testid="ct-tel" />
            </Field>
            <Field label="Correo de contacto" testid="ct-correo-field">
              <TextInput value={form.correo} onChange={(v) => set("correo", v)} type="email" placeholder="tienda@correo.com" testid="ct-correo" />
            </Field>
          </div>
          <Field label="Enlace a portafolio o sitio web" hint="Opcional" testid="ct-web-field">
            <TextInput value={form.web} onChange={(v) => set("web", v)} placeholder="https://…" testid="ct-web" />
          </Field>
        </section>

        {/* Categorías */}
        <section className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-soft p-6">
          <h2 className="text-sm font-bold uppercase tracking-widest text-slate-500">Especialidades o categorías <span style={{ color: MKT_GOLD }}>*</span></h2>
          <p className="text-xs text-slate-400 mt-1">Elige las categorías de productos digitales que ofrecerás.</p>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="ct-categorias">
            {CATEGORIAS_DIGITALES.map((c) => {
              const on = form.categorias.includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggleCat(c.id)}
                  data-testid={`ct-cat-${c.id}`}
                  aria-pressed={on}
                  className="relative text-left rounded-xl border bg-white p-3 flex items-center gap-2.5 transition-all"
                  style={on ? { borderColor: MKT_GOLD, boxShadow: `0 0 0 2px ${MKT_GOLD}22` } : { borderColor: "#E2E8F0" }}
                >
                  <span className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: MKT_TINT, color: MKT_GOLD }}>
                    <c.Icon size={17} />
                  </span>
                  <span className="text-xs font-semibold text-slate-700 leading-tight">{c.label}</span>
                  {on && (
                    <span className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: MKT_GOLD, color: "#fff" }}>
                      <Check size={12} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {/* Términos */}
        <label className="mt-6 flex items-start gap-3 bg-white rounded-2xl border border-slate-200 shadow-soft p-5 cursor-pointer" data-testid="ct-terminos-field">
          <input
            type="checkbox"
            checked={form.terminos}
            onChange={(e) => set("terminos", e.target.checked)}
            className="mt-0.5 w-5 h-5 accent-amber-600"
            data-testid="ct-terminos"
          />
          <span className="text-sm text-slate-600">
            <b className="text-slate-800">Acepto los términos para vendedores</b> de ConstruLink Marketplace. Confirmo que ofreceré únicamente productos digitales de mi autoría o con derechos de distribución.
          </span>
        </label>

        <div className="mt-4 flex items-start gap-2 text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-xl p-3">
          <Info size={15} className="mt-0.5 flex-shrink-0" style={{ color: MKT_GOLD }} />
          Al crear tu tienda, se enviará a revisión del equipo de ConstruLink antes de activarse.
        </div>

        {/* Acciones */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <button onClick={() => navigate(-1)} data-testid="ct-cancelar-btn" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-slate-300 text-slate-700 font-semibold hover:bg-slate-100 transition-colors">
            Cancelar
          </button>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button onClick={borrador} data-testid="ct-borrador-btn" className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-slate-300 text-slate-700 font-semibold hover:bg-slate-100 transition-colors">
              <Save size={16} /> Guardar como borrador
            </button>
            <button onClick={crear} data-testid="ct-crear-btn" className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-white font-bold shadow-sm hover:brightness-110 transition-all" style={{ backgroundColor: MKT_GOLD }}>
              {editando ? "Guardar cambios" : "Crear mi tienda"} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
